import React from "react";
import { observer } from "mobx-react";
import {autorun, reaction} from "mobx";
import EsriLegendViewModel from "@arcgis/core/widgets/Legend/LegendViewModel";
import EsriWidget from "@arcgis/core/widgets/Widget";

import Handles from "@arcgis/core/core/Handles";
import Collection from "@arcgis/core/core/Collection";
import ActiveLayerInfo from "@arcgis/core/widgets/Legend/support/ActiveLayerInfo";
import * as esriSymbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import * as arcade from "@arcgis/core/arcade";
import _ from "lodash";

import "./legend.scss";

import { UiState } from "../../ui-state";
import { PathUtils } from "../../../utils/path-utils";

import { LegendConfiguration } from "../../config/legend-configuration";
import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {Message} from "semantic-ui-react";
import {MessageConfiguration} from "../../config/message-configuration";
import {MapState} from "../../map-state";
import {LegendPatch} from "./legendPatch";
import {ListItemConfiguration} from "../../config/list-item-configuration";
import {AppState} from "../../../stores/app-mobx";
import {ConditionalLinkConfiguration} from "../../config/link-configuration";

const CSS = {
    base: "esri-legend",
    widget: "esri-widget",
    panel: "esri-widget--panel",
    widgetIcon: "esri-icon-layer-list",
    service: "esri-legend__service",
    label: "esri-legend__service-label",
    layer: "esri-legend__layer",
    groupLayer: "esri-legend__group-layer",
    groupLayerChild: "esri-legend__group-layer-child",
    layerTable: "esri-legend__layer-table",
    layerTableSizeRamp: "esri-legend__layer-table--size-ramp",
    layerChildTable: "esri-legend__layer-child-table",
    layerCaption: "esri-legend__layer-caption",
    layerBody: "esri-legend__layer-body",
    layerRow: "esri-legend__layer-row",
    layerInfo: "esri-legend__layer-cell esri-legend__layer-cell--info",
    symbolContainer: "esri-legend__layer-cell esri-legend__layer-cell--symbols",
    symbol: "esri-legend__symbol",
    sizeRamp: "esri-legend__size-ramp",
    colorRamp: "esri-legend__color-ramp",
    opacityRamp: "esri-legend__opacity-ramp",
    // common
    header: "esri-widget__heading",
    hidden: "esri-hidden",
    // geo
    geoGroup: "geo-group",
    geoInvisible: "geo-invisible",
    geoHidden: "geo-hidden",
    geoLayerNotSelected: "geo-legend-layer-not-selected",
    geoLegendDisabled: "geo-legend-disabled"
};

interface LegendProps {
    uiStore: UiState;
    config: LegendConfiguration;
    controlsConfig: SidebarControlsConfiguration;
    appStore: AppState;
    mapStore: MapState;
    view: __esri.MapView | __esri.SceneView | undefined;
    keyAppend?: string | number | undefined;
    layerInfos?: any;
    onLoad?: (legendViewModel: EsriLegendViewModel) => any | undefined;
    small?: boolean;
    topicSwitcher?: JSX.Element | null;
}
/* Basierend auf der Custom Legend von Sascha Brunner und der Esri Legend.
Es wird nur der style.type classic unterstützt!
https://github.com/Esri/arcgis-js-api/blob/4master/widgets/Legend.tsx
https://github.com/Esri/arcgis-js-api/blob/4master/widgets/Legend/styles/Classic.tsx

@todo elevation und AV Liegenschaften diff, projektiert machen zusammen Probleme. Vermutlich ein Problem welches alle
   ImageryLayer betrifft.
*/
@observer
export class Legend extends React.Component<LegendProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    initialOrder: Map<string, string[][]> = new Map();
    _handles: Handles = new Handles();
    viewModel: EsriLegendViewModel;
    activeLayerInfos: Collection<ActiveLayerInfo>;
    widgetKey = "legend";
    constructor(props: LegendProps) {
        super(props);
        this.state = {
            activeLayerInfos: null,
            postMessages: [],
            preMessages: [],
            links: [],
            visible: true,
            isLoading: false,
            version: 0,
            layersReady: []
        };
        if (typeof this.props.keyAppend === "string" || typeof this.props.keyAppend === "number") {
            this.widgetKey += "-" + this.props.keyAppend.toString();
        }
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
    }
    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            const {view, uiStore} = this.props;
            if (uiStore.widgets.has(this.widgetKey)) {
                r.dispose(); // stop autorun
                return;
            }
            if (!view) {
                return;
            }
            this.viewModel = this.loadLegend(view);
            this.setState({activeLayerInfos: this.reorder(this.viewModel.activeLayerInfos)});
            this.watchHandles.push(
                this.viewModel.activeLayerInfos.on("change",()=> {
                    this.setState({activeLayerInfos: this.reorder(this.viewModel.activeLayerInfos)});
                    this._refreshActiveLayerInfos(this.viewModel.activeLayerInfos);
                })
            );
            this.watchHandles.push(
                // TODO: Sollte nur ausgelöst werden, wenn es auch tatsächlich eine Änderung gab
                view.watch("scale",()=> {
                    this.scheduleRender();
                })
            );
            this.setState({
                isLoading: false
            });
            if (this.viewModel.activeLayerInfos) {
                this._refreshActiveLayerInfos(this.viewModel.activeLayerInfos);
            }
            uiStore.addWidget(this.widgetKey, this);

            r.dispose();
        }));
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.props.mapStore.activeLayerMetauids, () => {
            this.updateMessages();
        }));
    }
    reorder(activeLayerInfos: any){
        const {config} = this.props;
        const layerListConfig = this.props.controlsConfig.layerList;
        if(activeLayerInfos){
            if(layerListConfig?.rebuild && activeLayerInfos.length > 0) {
                config.layerInfos = [];
                const subIds: number[] = [];
                // layer IDs aus Konfiguration lesen und für MapImageLayer aufbereiten
                layerListConfig.items.forEach((i) => {
                    if(i.layer.id) {
                        subIds.push(parseInt(i.layer.id?.split(/-/)[1] as string, 10));
                    }
                });

                activeLayerInfos.forEach((activeLayerInfo: __esri.ActiveLayerInfo)=>{
                    if(activeLayerInfo.layer.type === "map-image") {
                        activeLayerInfo.sublayerIds = subIds.reverse();
                        this.watchHandles.push(reactiveUtils.when(
                            () => activeLayerInfo.ready,
                            () => {
                                activeLayerInfo.legendElements =
                                    this.groupLegendElements(activeLayerInfo.legendElements);
                            },
                            { initial: true, once: true }
                        ));
                    }
                });
            }
            // Layer mit ausgeschalteter Legende rausfiltern
            activeLayerInfos = activeLayerInfos.filter((item: any) => item.layer?.legendEnabled);
            activeLayerInfos = activeLayerInfos
                .toArray()
                .map((item: any, index: number) => {
                    if ("metauid" in item.layer) { // TODO: Funktioniert eventuell nicht und muss noch getestet werden.
                        item.metauid = item.layer.metauid;
                    }else {
                        const url = PathUtils.getLayerPath(item.layer);
                        if (typeof url === "string") {
                            item.metauid = PathUtils.getMetaId(url);
                        }else {
                            item.metauid = null;
                        }
                    }
                    if (config.reorder) {
                        item.sort = config.reorder.indexOf(item.metauid);
                        if (item.sort < 0) {
                            item.sort = index + activeLayerInfos.length;
                        }
                    } else {
                        item.sort = index;
                    }
                    return item;
                });
            activeLayerInfos = _.sortBy(activeLayerInfos,["sort"]);
        }
        return activeLayerInfos;
    }

    /**
     * Behält die ursprüngliche Legendenpatch-Sortierung bei. Das sich die Sortierung verändert ist mit hoher
     * Wahrscheinlichkeit ein Esri Bug
     *
     * @param activeLayerInfo
     * @param metauid
     */
    keepInitialOrder(activeLayerInfo: __esri.ActiveLayerInfo, metauid: string): __esri.ActiveLayerInfo {
        let order: string[][] | undefined;
        if(!this.initialOrder.get(metauid)){
            order = [];
            activeLayerInfo.legendElements.forEach((element: __esri.LegendElement) => {
                if(element.infos  && typeof element.infos[0] !== "undefined"
                    && typeof order !== "undefined"){
                    if(typeof element.infos[0].infos !== "undefined"){
                        order.push(element.infos[0].infos.map((info: __esri.SymbolTableElementInfo) =>
                            (info.label)? info.label.toString(): ""));
                    } else {
                        order.push(element.infos.map((info) => (info.label)? info.label.toString(): ""));
                    }

                }
            });
            if(order.length > 0){
                this.initialOrder.set(metauid, order);
            }
            this.initialOrder.set(metauid, order);
        } else {
            order = this.initialOrder.get(metauid);
        }
        if (typeof order !== "undefined" && order.length > 0){
            activeLayerInfo.legendElements.forEach((element: __esri.LegendElement, index) => {
                if(element.infos && typeof element.infos[0] !== "undefined"
                    && typeof order !== "undefined" && typeof order[index] !== "undefined"){
                    if(typeof element.infos[0].infos !== "undefined"){
                        const infoLength = element.infos[0].infos.length;
                        element.infos[0].infos.forEach((info: any, i: number) => {
                            info.sort = order? order[index].indexOf(info.label?.toString()): -1;
                            if (info.sort < 0) {
                                info.sort = i + infoLength;
                            }
                        });
                        element.infos[0].infos = _.sortBy(element.infos[0].infos,["sort"]);
                    } else {
                        const infoLength = element.infos.length;
                        element.infos.forEach((info: any, i: number) => {
                            info.sort = order? order[index].indexOf(info.label): -1;
                            if (info.sort < 0) {
                                info.sort = i + infoLength;
                            }
                        });
                        element.infos = _.sortBy(element.infos,["sort"]);
                    }
                }
            });
        }
        return activeLayerInfo;
    }
    loadLegend(view: __esri.MapView | __esri.SceneView): EsriLegendViewModel {
        const config = this.props.config as __esri.LegendViewModelProperties;
        if (typeof this.props.layerInfos !== "undefined") {
            config.layerInfos = this.props.layerInfos.map((layerInfo: any) => {
                // Falls MapImageLayer entsprechend Aufbereiten
                if(layerInfo.layer.hasOwnProperty("layer")){
                    layerInfo.sublayerIds = [];
                    if (layerInfo.layer.legendEnabled) {
                        layerInfo.sublayerIds.push(layerInfo.layer.id);
                    }
                    if(layerInfo.layer.sublayers && layerInfo.sublayerIds.length === 0) {
                        layerInfo.layer.sublayers.flatten(
                            (sub: __esri.Sublayer) => sub.sublayers).forEach((sub: __esri.Sublayer) => {
                            if(sub.legendEnabled){
                                layerInfo.sublayerIds.push(sub.id);
                            }
                        });
                    }
                    layerInfo.layer = layerInfo.layer.layer;
                }
                return layerInfo;
            });
        }
        const props = {
            view,
            ...config
        };
        return new EsriLegendViewModel(props);
    }
    groupLegendElements(elements: __esri.LegendElement[]): __esri.LegendElement[] {
        const layerListConfig = this.props.controlsConfig.layerList;
        if(layerListConfig.rebuild && elements[0] && !(elements[0] as any).processed){
            elements.forEach((element: any) => {
                const item = layerListConfig.items.find((i) => i.title === element.title);
                if (item) {
                    element.index = item.index;
                    if(item.layer.id) {
                        // ergänze Layer ID, falls diese vorhanden ist
                        element.layerId = parseInt(item.layer.id?.split(/-/)[1] as string, 10);
                    }
                    if(item.parent) {
                        const p = layerListConfig.groups.find((g) => g.layer.id === item.parent.layer.id);
                        if (p) {
                            element.group = {title: p.title, id: p.layer.id};
                        }
                    }
                }
            });
            const groups = this.buildHierarchy(layerListConfig.groups);
            return this.addGroups(groups, elements);

        }
        return elements;
    }
    addGroups(groups: any[], elements: __esri.LegendElement[], sub?: boolean): __esri.LegendElement[] {
        const layerListConfig = this.props.controlsConfig.layerList;
        const legendElements: __esri.LegendElement[] = [];
        groups.forEach((g, index)=>{
            const legendElement: __esri.LegendElement = {
                type: "symbol-table",
                title: g.title,
                infos: []
            };
            (legendElement as any).group = {title: g.title, id: g.layer.id, isGroup: true};
            (legendElement as any).processed = true;
            if(g.hasOwnProperty("index")) {
                (legendElement as any).index = g.index;
            } else {
                (legendElement as any).index = index;
            }
            if(g.children) {
                (legendElement as any).infos = this.addGroups(g.children, elements, true);
            }
            if(elements) {
                elements.filter((e: any) => e.group?.id === g.layer.id)?.forEach((e) => {
                    (e as any).processed = true;
                    (legendElement as any).infos.push(e);
                });
            }
            legendElements.push(legendElement);
        });
        if(!sub) {
            // Ergänze Items welche zu keiner Gruppe gehören
            layerListConfig.items.filter((i) => !i.hasOwnProperty("parent"))?.forEach((li, index) => {
                const legendElement: __esri.LegendElement = {
                    type: "symbol-table",
                    title: li.title,
                    infos: []
                };
                (legendElement as any).processed = true;
                if (li.hasOwnProperty("index")) {
                    (legendElement as any).index = li.index;
                } else {
                    (legendElement as any).index = index + groups.length;
                }
                if (elements) {
                    const item = elements.find((e) => e.title === li.title);
                    if (item) {
                        (item as any).processed = true;
                        (legendElement as any).infos.push(item);
                    }
                }
                legendElements.push(legendElement);
            });
        }
        return legendElements;
    }
    buildHierarchy(groups: ListItemConfiguration[]): [] {
        const children: any = {};
        const roots: [] = [];

        // find the top level nodes and hash the children based on parent
        groups.forEach((groupConfig: ListItemConfiguration) => {
            const p = groupConfig.parent;
            (groupConfig as any).children  = [];
            let target: any;
            if (p && p.layer.id) {
                target = (children[p.layer.id] || (children[p.layer.id] = []));
            } else {
                target = roots;
            }
            target.push(groupConfig);
        });

        // function to recursively build the tree
        const findChildren: any = (parent: any) => {
            if (children[parent.layer.id]) {
                parent.children = children[parent.layer.id];
                for (let i = 0, len = parent.children.length; i < len; ++i) {
                    findChildren(parent.children[i]);
                }
            }
        };

        // enumerate through to handle the case where there are multiple roots
        for (let i = 0, len = roots.length; i < len; ++i) {
            findChildren(roots[i]);
        }

        return roots;
    }
    updateMessages(){
        const {controlsConfig} = this.props;
        const preMessages = this.prepareMessagesContent(controlsConfig.legend.preContent);
        if(preMessages.length>0) {
            Promise.all(preMessages).then((msgs) => {
                this.setState({preMessages: msgs.filter((m) => m)});
            });
        }
        const messages = this.prepareMessagesContent(controlsConfig.legend.postContent);
        if(messages.length>0) {
            Promise.all(messages).then((msgs) => {
                this.setState({postMessages: msgs.filter((m) => m)});
            });
        }
        const links = this.prepareMessagesContent(controlsConfig.legend.links);
        if(links.length>0) {
            Promise.all(links).then((msgs) => {
                this.setState({links: msgs.filter((m) => m)});
            });
        }
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[] | ConditionalLinkConfiguration[]):
    Array<Promise<JSX.Element | undefined>> {
        const messagePromises: Array<Promise<JSX.Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }, {
                    name: "$activeLayersMeta",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        if(message instanceof ConditionalLinkConfiguration
                            || (message as unknown as ConditionalLinkConfiguration).url){
                            resolve(<div className={"geo-legend-file"} key={index}>
                                <a className={message.class} href={(message as ConditionalLinkConfiguration).url}
                                    target={"_blank"} rel={"noreferrer"}>
                                    <span className={"linked"}>{message.title}</span></a></div>);
                        }else{
                            resolve(this.prepareMessageContent(message, index));
                        }
                    }));
                } else {
                    if(this.props.mapStore.activeLayerMetauids) {
                        const activeLayers: string[] = [];
                        this.props.mapStore.activeLayers.forEach((layerNumbers: number[], layerMeta) => {
                            layerNumbers?.forEach((lNo)=>{
                                activeLayers.push(layerMeta+"-"+(lNo as string));
                            });
                        });
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: activeLayers,
                                    $activeLayersMeta: this.props.mapStore.activeLayerMetauids,
                                    $subtopicId: (this.props.appStore.subtopicId? this.props.appStore.subtopicId: "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    if(message instanceof ConditionalLinkConfiguration
                                        || (message as unknown as ConditionalLinkConfiguration).url){
                                        m = <div className={"geo-legend-file"} key={index}>
                                            <a className={message.class}
                                                href={(message as ConditionalLinkConfiguration).url}
                                                target={"_blank"} rel={"noreferrer"}>
                                                <span className={"linked"}>{message.title}</span></a></div>;
                                    }else{
                                        m = this.prepareMessageContent(message, index);
                                    }
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number): JSX.Element {
        let message: JSX.Element;
        // quick and dirty!
        const messageContent = <p
            dangerouslySetInnerHTML={{__html: messageConfig.content}}/>;
        if (messageConfig.class === "blank"){
            message = <div
                key={index}
                className={messageConfig.class ? messageConfig.class : undefined}
            >{messageContent}</div>;
        } else {
            message = <div className={"geo-message-wrapper"} key={index}><Message
                attached="bottom"
                className={messageConfig.class ? messageConfig.class as string: ""}
                content={messageContent}
            /></div>;
        }
        return message;
    }

    componentWillUnmount(): void {
        const {uiStore} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this._handles.removeAll();
        uiStore.removeWidget(this.widgetKey);
    }
    render(): JSX.Element | null {
        const {activeLayerInfos, links, postMessages, preMessages, version} = this.state;
        const {controlsConfig, topicSwitcher} = this.props;
        if (!activeLayerInfos) {
            return null;
        }
        const filteredLayers =
                activeLayerInfos &&
                activeLayerInfos
                    .map((activeLayerInfo: __esri.ActiveLayerInfo) => this._renderLegendForLayer(activeLayerInfo))
                    .filter((layer: __esri.Layer) => !!layer);
        return (
            <div className={"geo-legend-panel"}>
                {topicSwitcher}
                {preMessages}
                <div key={version} className={"geo-legend-wrapper "+ CSS.base + " "+ CSS.panel}>
                    {filteredLayers && filteredLayers.length ? (
                        filteredLayers
                    ) : (controlsConfig.legend.preContent || controlsConfig.legend.postContent? null:
                        (<div className={CSS.service}>Keine Legende vorhanden</div>)
                    )}
                </div>
                {postMessages}
                {links}
            </div>
        );
    }
    classes(className: string, classNames?: string | string[] | object): string {
        const widget = new EsriWidget();
        const classString = widget.classes(className, classNames);
        return classString;
    }
    prepareLegendElements( elements: any[], layer: __esri.Layer, rootMetauid?: string, overwrite?: any): any[] {
        const {config} = this.props;
        if (overwrite && overwrite.removeLabels) {
            elements = elements.filter((element) => overwrite.removeLabels.indexOf(element.label) === -1);
        }
        elements.forEach((element, index) => {
            if(typeof element.index !== "number"){
                element.index = index;
            }
            const titleObj = element.title;
            let title: string | null = null;
            if (typeof titleObj === "string") {
                title = titleObj;
            } else if (titleObj) {
                title = (titleObj as any).field;
            }
            // append für Sublayer in MapImageLayer ermöglichen
            let elementOverwrite = overwrite;
            if (rootMetauid && element.layerId &&  config.overwriteLayerInfos
                && config.overwriteLayerInfos[rootMetauid+"-"+element.layerId]) {
                elementOverwrite = config.overwriteLayerInfos[rootMetauid+"-"+element.layerId];
            }
            if (elementOverwrite && typeof elementOverwrite.append !== "undefined" && element.infos) {
                const append = elementOverwrite.append as __esri.SymbolTableElementInfo[];
                if(typeof element.infos !== "undefined" && Array.isArray(element.infos)){
                    append.forEach((current: __esri.SymbolTableElementInfo) => {
                        if(element.infos && Array.isArray(element.infos)) {
                            const x = (element.infos as __esri.SymbolTableElementInfo[]).find(
                                (item: __esri.SymbolTableElementInfo) => item.label === current.label);
                            if (!x) {
                                element.infos.push(current);
                            }
                        }
                    });
                }
            }
            if (typeof element.label !== "undefined") {
                if (elementOverwrite && elementOverwrite.labels && elementOverwrite.labels[element.label]) {
                    element.label = elementOverwrite.labels[element.label];
                }
            }
            if (typeof title !== "undefined") {
                if (elementOverwrite && title) {
                    if (elementOverwrite.removeLabels && elementOverwrite.removeLabels.indexOf(title) > -1) {
                        element.title = null; // Zum Ausblenden kennzeichnen
                    } else if (elementOverwrite.labels && elementOverwrite.labels[title]) {
                        element.title = elementOverwrite.labels[title];
                    }
                }
            }
            if (layer.hasOwnProperty("maxScale")) {
                element.maxScale = (layer as any).maxScale;
                element.minScale = (layer as any).minScale;
            }
            if (layer.hasOwnProperty("visible")) {
                element.visible = (layer as any).visible;
            }
            if (layer.hasOwnProperty("allSublayers")) {
                const tmpLayer = (layer as any).allSublayers.find((l: __esri.Layer) => {
                    // Falls vorhanden prüfung auf Layer ID
                    if(typeof element.layerId === "number") {
                        return element.layerId === l.id;
                    }else{
                        return l.title === title;
                    }
                });
                if (tmpLayer) {
                    element.maxScale = tmpLayer.maxScale;
                    element.minScale = tmpLayer.minScale;
                    element.visible = tmpLayer.visible;
                    if (elementOverwrite
                        && tmpLayer.metauid
                        && elementOverwrite.reorder
                        && elementOverwrite.reorder.indexOf(tmpLayer.metauid) > -1) {
                        element.index = elementOverwrite.reorder.indexOf(tmpLayer.metauid);
                    }
                    if(tmpLayer.metauid) {
                        element.metauid = tmpLayer.metauid;
                    }
                }
            }
            if (element.hasOwnProperty("infos")){
                element.infos = this.prepareLegendElements(element.infos, layer, rootMetauid, overwrite);
            }
        });
        return elements.sort((a, b) => a.index - b.index);
    }
    scheduleRender(): void {
        this.setState((state:any) => ({version: state.version + 1}));
    }
    // -------------------------------------------------------------------
    //
    //  Private methods
    //
    // -------------------------------------------------------------------

    // Important
    // watch for changes in each ActiveLayerInfo
    private _refreshActiveLayerInfos(activeLayerInfos: Collection<ActiveLayerInfo>): void {
        this._handles.removeAll();
        activeLayerInfos.forEach((activeLayerInfo) =>
            this._renderOnActiveLayerInfoChange(activeLayerInfo)
        );
        this.scheduleRender(); // This triggers widget's DOM refresh. calls "render()" method
    }

    // Important
    // watch for activeLayerInfo.version changes
    // Also watch for child activeLayerInfos if available
    private _renderOnActiveLayerInfoChange(activeLayerInfo: ActiveLayerInfo): void {
        const infoVersionHandle = reactiveUtils.watch(
            () => activeLayerInfo.version,
            () => {
                if(activeLayerInfo.ready && activeLayerInfo.layer.type === "map-image") {
                    activeLayerInfo.legendElements =
                        this.groupLegendElements(activeLayerInfo.legendElements);
                }
                this.scheduleRender();
            }, // This triggers widget's DOM refresh. calls "render()" method
            { initial: true }
        );
        this._handles.add(infoVersionHandle, `version_${(activeLayerInfo.layer)?(activeLayerInfo.layer as any).uid: "1"}`);

        activeLayerInfo.children.forEach((childActiveLayerInfo) =>
            this._renderOnActiveLayerInfoChange(childActiveLayerInfo)
        );
    }

    // render ActiveLayerInfo
    // This can be based on your UI requirement
    private _renderLegendForLayer(activeLayerInfo: ActiveLayerInfo): JSX.Element | null {
        const {config} = this.props;
        if (!activeLayerInfo || !activeLayerInfo.ready) {
            return null;
        }
        const hasChildren = !!activeLayerInfo.children.length;
        const key = `legend-${activeLayerInfo.layer.id}-version-${activeLayerInfo.version}`;

        let labelNode = activeLayerInfo.title ? (
            <h3 className={this.classes(CSS.header, CSS.label)}>{activeLayerInfo.title}</h3>
        ) : null;

        // title als slug in class ergänzen
        let addClass = "";
        if (activeLayerInfo.title) {
            addClass += " " + PathUtils.slug(activeLayerInfo.title);
        }
        let metauid = "";
        if ("metauid" in activeLayerInfo.layer) {
            metauid = (activeLayerInfo.layer as any).metauid;
        }else if ("url" in activeLayerInfo.layer) {
            const url = PathUtils.getLayerPath(activeLayerInfo.layer);
            if (typeof url === "string") {
                metauid = PathUtils.getMetaId(url);
            }
        }
        if(metauid){
            activeLayerInfo = this.keepInitialOrder(activeLayerInfo, metauid);
            addClass += " " + PathUtils.slug(metauid);
            if (config.overwriteLayerInfos && config.overwriteLayerInfos[metauid]) {
                const elementConfig = config.overwriteLayerInfos[metauid];
                activeLayerInfo.legendElements =
                    this.prepareLegendElements(activeLayerInfo.legendElements, activeLayerInfo.layer,
                        metauid, elementConfig);
                if (typeof elementConfig.append !== "undefined") {
                    const append = elementConfig.append as __esri.SymbolTableElementInfo[];
                    const element: __esri.LegendElement =
                        activeLayerInfo.legendElements[activeLayerInfo.legendElements.length -1];
                    if(typeof element !== "undefined" && Array.isArray(element.infos)){
                        append.forEach((current: __esri.SymbolTableElementInfo) => {
                            if(element.infos && Array.isArray(element.infos)) {
                                const x = (element.infos as __esri.SymbolTableElementInfo[]).find(
                                    (item: __esri.SymbolTableElementInfo) => item.label === current.label);
                                if (!x) {
                                    element.infos.push(current);
                                }
                            }
                        });
                    }
                }
                if (labelNode) {
                    if(typeof elementConfig.removeLabels !== "undefined"
                        && elementConfig.removeLabels.indexOf(activeLayerInfo.title) > -1) {
                        labelNode = null;
                    } else if (elementConfig.labels && elementConfig.labels[activeLayerInfo.title]) {
                        labelNode = <h3 className={this.classes(CSS.header, CSS.label)}>
                            {elementConfig.labels[activeLayerInfo.title]}
                        </h3>;
                    }
                }
            } else {
                this.prepareLegendElements(activeLayerInfo.legendElements, activeLayerInfo.layer);
            }
        }
        if (hasChildren) {
            const layers = activeLayerInfo.children
                .map((childActiveLayerInfo) => this._renderLegendForLayer(childActiveLayerInfo))
                .toArray();

            return (
                <div key={key} className={this.classes(CSS.service, CSS.groupLayer) + addClass}>
                    {labelNode}
                    {layers}
                </div>
            );
        }

        const legendElements = activeLayerInfo.legendElements;
        if (legendElements && !legendElements.length) {
            return null;
        }
        const filteredElements = legendElements
            .map((legendElement) => this._renderLegendForElement(legendElement, activeLayerInfo.layer))
            .filter((element) => !!element);

        if (!filteredElements.length) {
            return null;
        }

        const layerClasses = {
            [CSS.groupLayerChild]: !!activeLayerInfo.parent,
            // Layer mit deaktivierter Legende entsprechend markieren
            [CSS.geoLegendDisabled]: !(activeLayerInfo.layerView as any).legendEnabled
        };

        return (
            <div key={key} className={this.classes(CSS.service, layerClasses) + addClass}>
                {labelNode}
                <div className={CSS.layer}>{filteredElements}</div>
            </div>
        );
    }

    // Render a LegendElement of ActiveLayerInfo
    private _renderLegendForElement(
        legendElement: any,
        layer: __esri.Layer,
        isChild?: boolean
    ): JSX.Element | null {
        const { view } = this.props;
        if (!view) {
            return null;
        }
        const isColorRamp = legendElement.type === "color-ramp";
        const isOpacityRamp = legendElement.type === "opacity-ramp";
        const isSizeRamp = legendElement.type === "size-ramp";

        let content: any = null;
        const titleClasses = {
            [CSS.layerCaption]: true
        };
        // build symbol table or size ramp
        if (legendElement.type === "symbol-table" || isSizeRamp) {
            const removedSameLabel = (legendElement.infos as any).reduce((acc: any, current: any) => {
                if (typeof current.label === "undefined"){
                    current.label = current.title;
                }
                const x = acc.find((item: any) => item.label === current.label);
                if (!x) {
                    return acc.concat([current]);
                } else {
                    return acc;
                }
            }, []);
            // Falls nur ein Childelement vorhanden ist, wird der Title versteckt.
            if (removedSameLabel.length === 1) {
                titleClasses[CSS.geoHidden] = true;
                // Falls das Child selber über keinen Titel verfühgt, wird dieser übertragen.
                if(typeof removedSameLabel[0].label === "undefined" || removedSameLabel[0].label === ""){
                    removedSameLabel[0].title = legendElement.title;
                    removedSameLabel[0].label = legendElement.title;
                }
            }
            const rows = removedSameLabel
                .map((info: any) =>
                    this._renderLegendForElementInfo(
                        info,
                        layer,
                        isSizeRamp,
                        legendElement.type
                    )
                )
                .filter((row: any) => !!row);
            if (rows.length) {
                content = <div className={CSS.layerBody}>{rows}</div>;
            }
        } else if (isColorRamp || isOpacityRamp) {
            content = this._renderLegendForRamp(legendElement, layer.opacity);
        }
        if (!content) {
            return null;
        }
        const titleObj = legendElement.title;
        let title: string | null = null;

        if (typeof titleObj === "string") {
            title = titleObj;
        } else if (titleObj) {
            title = (titleObj as any).field;
        }

        const tableClass = isChild ? CSS.layerChildTable : CSS.layerTable;
        const caption = title ? <div className={this.classes("", titleClasses)}>{title}</div> : null;

        const tableClasses = {
            [CSS.layerTableSizeRamp]: isSizeRamp || !isChild,
            // in der View nicht sichtbare (in TOC nicht selektiert) Layer entsprechend markieren
            [CSS.geoLayerNotSelected]: !(!legendElement.hasOwnProperty("visible") || legendElement.visible),
            // in der View nicht sichtbare Layer (minScale, maxScale) entsprechend markieren
            [CSS.geoInvisible]: !(!legendElement.hasOwnProperty("maxScale") || (
                (legendElement.minScale === 0 || legendElement.minScale >= view.scale)
                && legendElement.maxScale <= view.scale))
        };
        // title als slug in class ergänzen
        if (title) {
            tableClasses[PathUtils.slug(title)] = true;
        }
        if(legendElement.metauid){
            tableClasses[PathUtils.slug(legendElement.metauid)] = true;
        }
        if(legendElement.group){
            tableClasses[PathUtils.slug(legendElement.group.id)] = true;
            if(legendElement.group.isGroup) {
                tableClasses[CSS.geoGroup] = true;
            }
        }
        const key = `legend-${layer.id}-type-${legendElement.type}-element-${legendElement.index}` + Math.random();
        return (
            <div key={key} className={this.classes(tableClass, tableClasses)}>
                {caption}
                {content}
            </div>
        );
    }

    // Render an info of LegendElement
    private _renderLegendForElementInfo(
        elementInfo: any,
        layer: __esri.Layer,
        isSizeRamp: boolean,
        legendType: string
    ): JSX.Element | null {
        // nested
        if (elementInfo.type) {
            return this._renderLegendForElement(elementInfo, layer, true);
        }

        let content: any = null;

        // Use symbol preview if available on the info
        if (elementInfo.symbol && elementInfo.preview) {
            content = <LegendPatch elementInfo={elementInfo} />;
        } else if (elementInfo.src) {
            content = this._renderImage(elementInfo, layer);
        }

        if (!content) {
            return null;
        }
        // label als slug in class ergänzen
        let addClass = "";
        if (elementInfo.label) {
            addClass += " " + PathUtils.slug("label-" + elementInfo.label);
        }
        const key = `legend-${(layer as any).uid}-value-${elementInfo.value}
                        -lable-${(elementInfo.label? elementInfo.label: "undefined")}`;
        return (
            <div key={key} className={CSS.layerRow + addClass}>
                <div className={CSS.symbolContainer}>{content}</div>
                <div className={CSS.layerInfo}>
                    {elementInfo.label || ""}
                </div>

            </div>
        );
    }

    private _renderImage(
        elementInfo: any,
        layer: __esri.Layer
    ): any {
        const { label, src, opacity } = elementInfo;

        const dynamicStyles = {
            opacity: `${opacity != null ? opacity : layer.opacity}`
        };

        return (
            <img
                alt={label}
                src={src}
                width={elementInfo.width}
                height={elementInfo.height}
                style={dynamicStyles}
            />
        );
    }

    // Color ramp customization
    private _renderLegendForRamp(
        legendElement: any,
        opacity: number
    ): any {
        const rampStops: any[] = legendElement.infos;
        const isOpacityRamp = legendElement.type === "opacity-ramp";

        const rampDiv = legendElement.preview;
        const opacityRampClass = isOpacityRamp ? CSS.opacityRamp : "";
        rampDiv.className = `${CSS.colorRamp} ${opacityRampClass}`;

        if (opacity != null) {
            rampDiv.style.opacity = opacity.toString();
        }

        const labelsContent = rampStops.map((stop) => (
            <div key={stop.value}>{stop.label}</div>
        ));

        const colors: any[] = [];
        rampStops.forEach((stop) => {
            colors.push(stop.color);
        });

        const preview = esriSymbolUtils.renderColorRampPreviewHTML(colors, {
            align: "horizontal",
            gradient: false,
            width: 300,
            height: 10
        });

        return (
            <div style={{display: "flex", flexDirection: "column"}}>
                <div>{preview}</div>
                <div style={{display: "flex", justifyContent: "space-between", width: "300px", marginTop: "0.5em;"}}>
                    {labelsContent}
                </div>
            </div>
        );
    }
}
